'use client';

import React from 'react';
import { motion } from 'framer-motion';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const Thesis = () => {
  return (
    <>
      <Navbar />

      {/* Hero Section */}
      <section className="relative h-[70vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={`${process.env.PUBLIC_URL}/images/research/thesis/thumb.jpg`}
            alt="Reset Studio Research"
            className="w-full h-full object-cover opacity-70"
          />
          <div className="absolute inset-0 bg-black/60" />
        </div>

        <motion.div
          className="relative z-10 text-center px-6 max-w-4xl"
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1 }}
        >
          <h1 className="text-white text-4xl md:text-6xl font-light tracking-wide">
            Transresolutionary Legibility of Architecture
          </h1>

          <p className="mt-6 text-white/80 text-base md:text-lg font-light leading-relaxed">
            A research inquiry into rethinking architectural studio environments
            through spatial reduction, material honesty, and workflow clarity.
          </p>
        </motion.div>
      </section>

      {/* Research Meta */}
      <section className="py-16 px-6 max-w-5xl mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-10 text-sm text-gray-600">
          <div>
            <p className="uppercase tracking-widest text-xs text-gray-400">
              Research Type
            </p>
            <p className="mt-2">Spatial & Practice-Based Research</p>
          </div>

          <div>
            <p className="uppercase tracking-widest text-xs text-gray-400">
              Status
            </p>
            <p className="mt-2">Ongoing</p>
          </div>

          <div>
            <p className="uppercase tracking-widest text-xs text-gray-400">
              Year
            </p>
            <p className="mt-2">2024 –</p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="pb-24 px-6 max-w-3xl mx-auto space-y-16">

        {/* Introduction */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <h2 className="text-2xl font-light text-gray-900">
            Introduction
          </h2>
          <p className="mt-6 text-gray-700 leading-relaxed text-base">
            Reset Studio began as a critical response to the contemporary
            architectural workplace—often overloaded with visual noise,
            excessive material expression, and inefficient spatial planning.
            This research investigates how subtractive design, disciplined
            material palettes, and spatial hierarchy can recalibrate focus,
            calm, and productivity within a studio environment.
          </p>
        </motion.div>

        {/* Research Questions */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <h2 className="text-2xl font-light text-gray-900">
            Core Research Questions
          </h2>

          <ul className="mt-6 space-y-4 text-gray-700 text-base list-disc list-inside">
            <li>
              How can spatial reduction improve cognitive clarity in design
              studios?
            </li>
            <li>
              What role do honest, unfinished materials play in long-term
              workspace adaptability?
            </li>
            <li>
              Can a studio function as both a workplace and a research tool?
            </li>
          </ul>
        </motion.div>

        {/* Methodology */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <h2 className="text-2xl font-light text-gray-900">
            Methodology
          </h2>
          <p className="mt-6 text-gray-700 leading-relaxed text-base">
            The research follows a practice-based methodology combining spatial
            prototyping, material testing, and real-time occupation feedback.
            Design decisions are tested through daily use, allowing the studio
            itself to act as a living laboratory rather than a finished object.
          </p>
        </motion.div>

        {/* Image Section */}
        <motion.div
          className="space-y-6"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <img
            src={`${process.env.PUBLIC_URL}/images/research/resetstudio/1.jpg`}
            alt="Reset Studio Process"
            className="w-full object-cover"
          />
          <p className="text-xs text-gray-500">
            Spatial prototype exploring open work zones and material restraint.
          </p>
        </motion.div>

        {/* Observations */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <h2 className="text-2xl font-light text-gray-900">
            Key Observations
          </h2>
          <p className="mt-6 text-gray-700 leading-relaxed text-base">
            Preliminary observations suggest increased focus duration, reduced
            visual fatigue, and greater flexibility in daily studio operations.
            The absence of excessive finishes allows material ageing to become
            part of the spatial narrative rather than a defect.
          </p>
        </motion.div>

        {/* Conclusion */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <h2 className="text-2xl font-light text-gray-900">
            Ongoing Direction
          </h2>
          <p className="mt-6 text-gray-700 leading-relaxed text-base">
            Reset Studio remains an open-ended research project. Future phases
            will expand into acoustic studies, lighting calibration, and
            long-term material weathering—feeding directly into built studio
            and workplace projects.
          </p>
        </motion.div>

      </section>

      <Footer />
    </>
  );
};

export default Thesis;
